"use client";

import { PropsWithChildren, useEffect, useRef } from "react";

import { authActions, contractActions } from "@/store/actions";
import { authSelector } from "@/store/auth/auth.slice";
import { networkSelector } from "@/store/network/network.slice";
import { usePathname, useRouter } from "next/navigation";
import cookie from "react-cookies";
import { useDispatch, useSelector } from "react-redux";
import { numberToHex } from "viem";
import { useAccount, useBalance, useDisconnect, useNetwork } from "wagmi";

import { twMerge } from "tailwind-merge";
import Footer from "../custom/footer";
import Header from "../custom/header";
import { LoginModal } from "../modals/login-modal";
import { useGlobalModal } from "../modals/modal-provider";
import { accountSelector } from "@/store/account/account.slice";
import NotSupportChain from "../modals/NotSupportChain";
import ChangeWalletModal from "../modals/change-wallet";

const tabList = [
  {
    hasLogin: false,
    path: "/",
    label: "Xác thực tài liệu",
    icon: "/icon-verify.svg",
    activeIcon: "/icon-verify-active.svg",
  },
  {
    hasLogin: true,
    path: "/upload",
    label: "Phát hành tài liệu",
    icon: "/icon-upload.svg",
    activeIcon: "/icon-upload-active.svg",
  },
  {
    hasLogin: false,
    path: "/guide",
    label: "Hướng dẫn",
    icon: "/icon-guide.svg",
    activeIcon: "/icon-guide-active.svg",
  },
];

const modalIdOps = "modalId-ops";

export const MainLayout = ({ children }: PropsWithChildren) => {
  const dispatch = useDispatch<any>();
  const { authLogin, loadingAuth } = useSelector(authSelector);
  const { openModal, closeModal } = useGlobalModal();
  const { account } = useSelector(accountSelector);
  const { isConnected, address: currentAddress, isConnecting } = useAccount();
  const { chain } = useNetwork();
  const hasShownModalRef = useRef(false);
  const { disconnect } = useDisconnect();
  const { listNetwork } = useSelector(networkSelector);
  const pathname = usePathname();
  const router = useRouter();
  const { data } = useBalance({
    address: currentAddress,
  });

  useEffect(() => {
    if (!!cookie.load("refresh_token")) {
      dispatch(authActions.authenticate());
    }
  }, []);

  useEffect(() => {
    if (isConnected) {
      dispatch(contractActions.loadContracts(numberToHex(chain?.id)));
    } else {
      listNetwork?.[0]?.chainId &&
        dispatch(contractActions.loadContracts(listNetwork?.[0]?.chainId));
    }
  }, [isConnected, chain, listNetwork]);

  useEffect(() => {
    if (account && isConnected && chain?.unsupported) {
      openModal({
        title: "Kiểm tra mạng của bạn",
        body: <NotSupportChain onCancel={closeModal} />,
        handleClose() {
          disconnect();
        },
      });
    }
  }, [chain, account, isConnected]);

  useEffect(() => {
    if (!account?.walletAddress || !currentAddress) return;

    const isWrongWallet =
      account?.walletAddress?.toLowerCase() !== currentAddress?.toLowerCase();

    if (isWrongWallet && isConnected && authLogin) {
      openModal({
        modalId: modalIdOps,
        title: "Opps, hình như có sai sót",
        description:
          "Địa chỉ ví mà bạn vừa kết nối khác với địa chỉ ví đã được liên kết trước. Vui lòng chuyển đổi địa chỉ ví đúng trong Ví được chọn.",
        body: (
          <ChangeWalletModal
            onCancel={() => {
              closeModal(modalIdOps);
            }}
          />
        ),
        handleClose() {
          disconnect();
        },
      });
    } else {
      closeModal(modalIdOps);
    }
  }, [currentAddress, isConnected, isConnecting, authLogin]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1 px-5 py-8 md:py-14">
        <div className="max-w-[1000px] mx-auto">
          <div className="hidden md:grid grid-cols-3 gap-4 p-1.5 bg-gray-100 rounded-xl h-[60px] mb-[54px]">
            {tabList.map((item) => {
              const active = pathname === item.path;
              return (
                <button
                  onClick={() => {
                    router.push(item.path);
                  }}
                  key={item.path}
                  className={twMerge(
                    "h-full flex items-center justify-center gap-2 px-4 py-2 rounded-md font-semibold transition-all text-xl",
                    active ? "bg-main text-white" : "text-main hover:text-main"
                  )}
                >
                  <img
                    className="w-7 h-7"
                    src={active ? item.activeIcon : item.icon}
                    alt={item.label}
                  />
                  {item.label}
                </button>
              );
            })}
          </div>

          <div className=" bg-white">{children}</div>
        </div>
      </div>

      <Footer />
    </div>
  );
};
